import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Hexagon, Lock, Mail, Play, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Asterisk, BentoTag } from "@/components/pitch/Bento";

const DEMO = [
  { label: "Admin", role: "Advisor", email: "admin@omnidraftlab.com", password: "Admin#12345" },
  { label: "Player", role: "Athlete", email: "player@omnidraftlab.com", password: "Player#12345" },
  { label: "Family", role: "Advisor", email: "family@omnidraftlab.com", password: "Family#12345" },
  { label: "Trainer", role: "Performance", email: "trainer@omnidraftlab.com", password: "Trainer#12345" },
];

export default function LoginPage() {
  useDocumentTitle("OmniDraftLab · Sign in");
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@omnidraftlab.com");
  const [password, setPassword] = useState("Admin#12345");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name}`);
      nav(u?.role === "family" ? "/app/family" : "/app/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (d) => {
    setEmail(d.email);
    setPassword(d.password);
  };

  return (
    <div className="min-h-screen w-full bg-[#04111A] text-1 relative overflow-hidden">
      {/* Background video — same treatment as the landing for visual continuity. */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-55"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/pitch/hero-poster.jpg"
        >
          <source src="/pitch/hero.webm" type="video/webm" />
          <source src="/pitch/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-[#04111A]/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#04111A]/20 to-[#04111A]/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#04111A]/30 via-transparent to-[#04111A]/30" />
      </div>

      {/* Top chrome — matches landing. */}
      <header className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" className="flex items-center gap-2.5" data-testid="login-logo">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 grid place-items-center shadow-[0_8px_24px_-8px_rgba(34,211,238,0.6)]">
            <Hexagon className="h-4 w-4 text-[#04111A]" strokeWidth={2.4} />
          </div>
          <span className="font-semibold tracking-tight text-1">OmniDraftLab</span>
        </Link>
        <Link
          to="/"
          data-testid="login-nav-home"
          className="text-2 hover:text-1 text-sm flex items-center gap-1.5 transition"
        >
          <ArrowLeft className="h-4 w-4" />
          Back home
        </Link>
      </header>

      {/* Sign-in stage — single centered glass card. */}
      <main className="relative z-10 min-h-screen flex items-center justify-center px-6 sm:px-10 py-24">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 0.65, 0.2, 1] }}
          className="w-full max-w-[460px] relative"
        >
          {/* Asterisk corner stamp — same brand mark used on landing & pitch */}
          <div className="absolute -top-3 -right-3 z-10">
            <Asterisk tone="rose" size={36} />
          </div>

          <div className="bento-glass p-8 sm:p-10 relative overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <BentoTag tone="cyan">Sign in</BentoTag>
              <Link
                to="/pitch"
                data-testid="login-pitch-deck-button"
                className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-300/30 bg-cyan-300/[0.08] hover:bg-cyan-300/[0.18] hover:border-cyan-300/50 transition"
              >
                <span className="grid place-items-center h-5 w-5 rounded-full bg-cyan-300 text-[#04111A] shadow-[0_0_14px_rgba(34,211,238,0.5)] group-hover:scale-105 transition">
                  <Play className="h-2.5 w-2.5 ml-0.5" fill="currentColor" />
                </span>
                <span className="text-cyan-100 text-[10px] font-semibold tracking-[0.18em] uppercase">Pitch deck</span>
              </Link>
            </div>

            {/* Editorial heading — smaller scale than landing but same family */}
            <h1
              className="font-semibold tracking-tight text-1 leading-[1.05] mt-6"
              style={{ fontSize: "clamp(2rem, 3.6vw, 3rem)" }}
            >
              Welcome <span className="text-cyan-300 italic font-light">back.</span>
            </h1>
            <p className="text-2 text-sm mt-2">Use a demo account or your credentials.</p>

            {/* Form */}
            <form onSubmit={onSubmit} className="mt-7 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-2 text-xs">Email</Label>
                <div className="relative">
                  <Mail className="h-4 w-4 text-3 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-9 h-11 bg-white/5 border-white/10 text-1"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-2 text-xs">Password</Label>
                  <Link to="/forgot-password" data-testid="login-forgot-password" className="text-cyan-300/80 hover:text-cyan-200 text-xs transition">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="h-4 w-4 text-3 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-9 h-11 bg-white/5 border-white/10 text-1"
                  />
                </div>
              </div>
              <Button
                data-testid="login-submit"
                type="submit"
                disabled={busy}
                className="w-full h-12 bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold rounded-2xl shadow-[0_18px_50px_-15px_rgba(34,211,238,0.7)] transition"
              >
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </form>

            {/* Demo accounts — refined into compact bento tiles */}
            <div className="mt-7 pt-5 border-t border-white/8">
              <div className="text-3 text-[10px] uppercase tracking-[0.22em] mb-3">Demo accounts</div>
              <div className="grid grid-cols-2 gap-2">
                {DEMO.map((d) => (
                  <button
                    key={d.email}
                    type="button"
                    onClick={() => fillDemo(d)}
                    data-testid={`login-demo-${d.label.toLowerCase()}`}
                    className="text-left p-2.5 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-cyan-300/30 transition group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-1 text-xs font-semibold">{d.label}</div>
                      <div className="text-3 text-[9px] uppercase tracking-[0.16em] group-hover:text-cyan-200 transition">{d.role}</div>
                    </div>
                    <div className="text-3 text-[10px] truncate mt-0.5">{d.email}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sub-card line — matches landing's eyebrow rhythm */}
          <div className="mt-5 text-3 text-[10px] uppercase tracking-[0.22em] flex items-center justify-center gap-3">
            <span>NFL-Caliber Evaluation</span>
            <span>·</span>
            <span>Application-only</span>
          </div>
        </motion.div>
      </main>

      <Toaster richColors theme="dark" position="top-right" />
    </div>
  );
}
